import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { LayoutDashboard, Monitor, CreditCard, Users, AlertCircle, Settings, Server, DollarSign, ChevronDown } from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import type { UserRole } from '../../types'

interface SidebarProps {
  userRole: UserRole | null
  adminLevel?: 'full_admin' | 'technician' | null
}

interface NavigationItem {
  name: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  permission?: string
  requiresFullAdmin?: boolean
  subItems?: Array<{
    name: string
    href: string
    icon: React.ComponentType<{ className?: string }>
  }>
}

const managerNavigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    name: 'Devices',
    href: '/devices',
    icon: Monitor,
    permission: 'VIEW_DEVICES'
  },
  {
    name: 'Billing',
    href: '/billing',
    icon: CreditCard,
    permission: 'VIEW_BILLING'
  },
  {
    name: 'Users',
    href: '/users',
    icon: Users,
    permission: 'VIEW_USERS'
  },
  {
    name: 'Issues',
    href: '/issues',
    icon: AlertCircle,
    permission: 'VIEW_TICKETS'
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    permission: 'MANAGE_SETTINGS'
  }
]

const employeeNavigation: NavigationItem[] = [
  {
    name: 'Issues',
    href: '/issues',
    icon: AlertCircle,
    permission: 'VIEW_TICKETS'
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    permission: 'MANAGE_SETTINGS'
  }
]

const billingNavigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/billing/dashboard',
    icon: LayoutDashboard,
  },
  {
    name: 'Payments',
    href: '/billing/payments',
    icon: DollarSign,
  },
  {
    name: 'Subscriptions',
    href: '/billing/subscriptions',
    icon: CreditCard,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    permission: 'MANAGE_SETTINGS'
  }
]

export const Sidebar = ({ userRole, adminLevel }: SidebarProps) => {
  const { hasPermission } = usePermissions({ userRole })
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Auto-expand sections based on current route
  useEffect(() => {
    const newExpanded = new Set(expandedItems)

    // Check if current path matches any sub-item
    if (location.pathname.startsWith('/billing/')) {
      newExpanded.add('Billing')
    } else {
      // Only remove if we navigate away from billing entirely
      if (!location.pathname.startsWith('/billing')) {
        newExpanded.delete('Billing')
      }
    }

    setExpandedItems(newExpanded)
  }, [location.pathname])

  const toggleExpanded = (itemName: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName)
    } else {
      newExpanded.add(itemName)
    }
    setExpandedItems(newExpanded)
  }

  const adminNavigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: 'Organizations',
      href: '/admin/organizations',
      icon: Users,
    },
    {
      name: 'Devices',
      href: '/admin/devices',
      icon: Monitor,
    },
    {
      name: 'Billing',
      icon: CreditCard,
      permission: 'VIEW_BILLING',
      subItems: [
        {
          name: 'Analytics',
          href: '/billing/dashboard',
          icon: LayoutDashboard
        },
        {
          name: 'Payments',
          href: '/billing/payments',
          icon: DollarSign
        },
        {
          name: 'Subscriptions',
          href: '/billing/subscriptions',
          icon: CreditCard
        }
      ]
    },
    {
      name: 'Issues',
      href: '/issues',
      icon: AlertCircle,
      permission: 'VIEW_TICKETS',
    },
    {
      name: 'Kaseya Settings',
      href: '/admin/kaseya/settings',
      icon: Server,
      requiresFullAdmin: true, // Only full admins can access Kaseya settings
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      permission: 'MANAGE_SETTINGS',
    },
  ]

  // Role-based navigation
  const navigationItems =
    userRole === 'admin' ? adminNavigation :
    userRole === 'employee' ? employeeNavigation :
    userRole === 'billing' ? billingNavigation :
    managerNavigation

  // Filter navigation items based on user role, permissions, and admin level
  const visibleItems = navigationItems.filter(item => {
    // Check if item requires full admin access
    if (item.requiresFullAdmin && userRole === 'admin' && adminLevel !== 'full_admin') {
      return false
    }

    // Check permission requirements
    if (!item.permission) return true
    return hasPermission(item.permission as any)
  })

  return (
    <div className="flex flex-col h-full bg-[#fafafa] text-[#3f3f46]">
      {/* Logo */}
      <div className="flex items-center px-6 py-4">
        <div className="flex items-center space-x-2">
          <img src="/black_logo.svg" alt="Helpdesk 411" className="h-10 md:h-16 w-auto" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {visibleItems.map((item) => {
          const Icon = item.icon
          const isExpanded = expandedItems.has(item.name)

          // If item has sub-items, render collapsible section
          if (item.subItems && item.subItems.length > 0) {
            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleExpanded(item.name)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-3xl text-sm font-medium text-[#3f3f46] hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isExpanded && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon
                      return (
                        <NavLink
                          key={subItem.name}
                          to={subItem.href}
                          className={({ isActive }) =>
                            `flex items-center px-3 py-2 rounded-2xl text-sm transition-colors ${
                              isActive
                                ? 'bg-[#EAEAEA] text-black font-medium'
                                : 'text-[#3f3f46] hover:bg-gray-100'
                            }`
                          }
                        >
                          <SubIcon className="w-4 h-4 mr-3" />
                          {subItem.name}
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          // Regular navigation item
          return (
            <NavLink
              key={item.name}
              to={item.href!}
              className={({ isActive }) =>
                `flex items-center px-3 py-3 rounded-3xl text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#EAEAEA] text-black'
                    : 'text-[#3f3f46] hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5 mr-3" />
              {item.name}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}
